export type WatchSource = 'page' | 'bulk' | 'notification';

export type ApplicationWatchRef = {
  applicationId: string;
  positionId: string;
  source: WatchSource;
  /** Epoch ms — omitted means no expiry. */
  expiresAt?: number;
};

const PAGE_GRACE_MS = 45_000;
const NOTIFICATION_GRACE_MS = 120_000;
const MAX_WATCH_ENTRIES = 60;

let pageRefs: ApplicationWatchRef[] = [];
let bulkRefs: ApplicationWatchRef[] = [];
const notificationRefs = new Map<string, ApplicationWatchRef>();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function dedupeRefs(refs: ApplicationWatchRef[]): ApplicationWatchRef[] {
  const now = Date.now();
  const byId = new Map<string, ApplicationWatchRef>();
  for (const ref of refs) {
    if (ref.expiresAt !== undefined && ref.expiresAt <= now) continue;
    const prev = byId.get(ref.applicationId);
    if (!prev) {
      byId.set(ref.applicationId, ref);
      continue;
    }
    const prevExpiry = prev.expiresAt ?? Number.POSITIVE_INFINITY;
    const nextExpiry = ref.expiresAt ?? Number.POSITIVE_INFINITY;
    if (nextExpiry > prevExpiry) {
      byId.set(ref.applicationId, ref);
    }
  }
  return Array.from(byId.values()).slice(0, MAX_WATCH_ENTRIES);
}

export function subscribeApplicationWatchSet(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Replace visible page rows — previous page refs get a short grace period. */
export function setPageWatchRefs(
  refs: Array<Pick<ApplicationWatchRef, 'applicationId' | 'positionId'>>,
): void {
  const now = Date.now();
  const nextIds = new Set(refs.map((r) => r.applicationId));
  const graceFromPrev = pageRefs
    .filter((r) => !nextIds.has(r.applicationId))
    .map((r) => ({
      ...r,
      source: 'page' as const,
      expiresAt: now + PAGE_GRACE_MS,
    }));

  pageRefs = dedupeRefs([
    ...refs.map((r) => ({ ...r, source: 'page' as const })),
    ...graceFromPrev,
  ]);
  notify();
}

export function clearPageWatchRefs(): void {
  const now = Date.now();
  pageRefs = pageRefs.map((r) => ({
    ...r,
    expiresAt: now + PAGE_GRACE_MS,
  }));
  notify();
}

export function setBulkWatchRefs(
  refs: Array<Pick<ApplicationWatchRef, 'applicationId' | 'positionId'>>,
): void {
  bulkRefs = refs.map((r) => ({ ...r, source: 'bulk' as const }));
  notify();
}

/** Wake an application into the watch set from a notification (B2 complement). */
export function addNotificationWatchRef(
  applicationId: string,
  positionId: string,
): void {
  notificationRefs.set(applicationId, {
    applicationId,
    positionId,
    source: 'notification',
    expiresAt: Date.now() + NOTIFICATION_GRACE_MS,
  });
  notify();
}

export function getMergedWatchRefs(): ApplicationWatchRef[] {
  const now = Date.now();
  for (const [id, ref] of notificationRefs) {
    if (ref.expiresAt !== undefined && ref.expiresAt <= now) {
      notificationRefs.delete(id);
    }
  }
  return dedupeRefs([
    ...pageRefs,
    ...bulkRefs,
    ...notificationRefs.values(),
  ]);
}

export function isApplicationInWatchSet(applicationId: string): boolean {
  return getMergedWatchRefs().some((r) => r.applicationId === applicationId);
}

export function watchSetKey(ref: Pick<ApplicationWatchRef, 'applicationId' | 'positionId'>): string {
  return `${ref.positionId}:${ref.applicationId}`;
}
